// Pitch-to-key verb — detect the source's fundamental, then resample so
// it lands on a target MIDI note. Length changes with pitch (resample-
// form, not phase-vocoder) — that's the explicit tradeoff vs the Mangle
// pitch block which does the same thing for arbitrary semitone offsets.

import { useCallback, useEffect, useState } from 'react';
import { applyBlock } from './mangleEffects';
import {
  detectPitch,
  hzToMidi,
  midiToNoteName,
  type PitchEstimate,
} from './pitchDetect';
import { fromPcm, toMono, toPcm } from '../../pipeline';
import { Slider } from '../Slider';
import { SourcePicker, type PickedSource } from '../SourcePicker';
import type { VerbProps } from '../types';

function DetectedRow({ detected }: { detected: PitchEstimate | null }) {
  // hzToMidi runs once here, not three times across the JSX. Trivial
  // perf win, larger clarity win.
  const noteName = detected
    ? midiToNoteName(Math.round(hzToMidi(detected.hz)))
    : null;
  return (
    <div className="bg-zinc-950 border border-zinc-800 px-3 py-2 text-[11px] text-zinc-300 flex items-center justify-between gap-2">
      <span>Detected</span>
      {detected && noteName ? (
        <span className="tabular-nums">
          {detected.hz.toFixed(1)} Hz ·{' '}
          <span className="text-yellow-300">{noteName}</span>{' '}
          <span className="text-zinc-500">
            · conf {(detected.confidence * 100).toFixed(0)}%
          </span>
        </span>
      ) : (
        <span className="text-zinc-500 italic">no clear pitch</span>
      )}
    </div>
  );
}

export function PitchVerb({
  audioContext,
  storage,
  onBuffer,
  onStatus,
}: VerbProps) {
  const [source, setSource] = useState<PickedSource | null>(null);
  const [detected, setDetected] = useState<PitchEstimate | null>(null);
  const [targetMidi, setTargetMidi] = useState(60); // C4
  const [rendering, setRendering] = useState(false);

  // Re-detect on source change. Mono-sum first so a hard-panned stereo
  // sample doesn't give different pitches per channel.
  useEffect(() => {
    if (!source) {
      setDetected(null);
      return;
    }
    const mono = toMono(toPcm(source.buffer)).channels[0]!;
    const result = detectPitch(mono, source.buffer.sampleRate);
    setDetected(result);
    if (result) setTargetMidi(Math.round(hzToMidi(result.hz)));
  }, [source]);

  const handleRender = useCallback(async () => {
    if (!source || !detected || !audioContext) return;
    if (detected.hz <= 0) return;
    const sourceMidi = hzToMidi(detected.hz);
    const semitones = targetMidi - sourceMidi;
    setRendering(true);
    try {
      // Existing pitch block in mangleEffects = OfflineAudioContext +
      // playbackRate resample. Pitch + duration both change — that's the
      // expected behaviour for "match my sample to a key", since the
      // user typically wants a single fixed-pitch one-shot anyway.
      const result = await applyBlock(
        toPcm(source.buffer),
        { kind: 'pitch', semitones },
        { ctx: audioContext, storage },
      );
      const buffer = fromPcm(audioContext, result);
      const sourceIds = source.sampleId ? [source.sampleId] : [];
      const targetNote = midiToNoteName(targetMidi);
      onBuffer(buffer, {
        suggestedName: `${source.name}_${targetNote}`,
        origin: {
          kind: 'pitch-to-key',
          sourceSampleId: source.sampleId,
          sourceHz: detected.hz,
          targetMidi,
        },
        // Pitched samples should remember their target pitch — that's
        // what `n("c4")` triggering plays at the right rate against.
        pitchedRootMidi: targetMidi,
        sourceSampleIds: sourceIds.length > 0 ? sourceIds : undefined,
      });
      onStatus('Rendered — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [source, detected, audioContext, storage, targetMidi, onBuffer, onStatus]);

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Detect the source's pitch and resample it to a target note. Length
        changes with pitch — works best on tonal one-shots (synth lead,
        plucked string, sung note).
      </div>
      <SourcePicker
        audioContext={audioContext}
        selectedId={source?.sampleId}
        onPicked={setSource}
        onStatus={onStatus}
        label="Source"
      />
      {source && <DetectedRow detected={detected} />}
      <Slider
        label="Target"
        value={targetMidi}
        min={24}
        max={96}
        step={1}
        display={`${midiToNoteName(targetMidi)} (MIDI ${targetMidi})`}
        onChange={setTargetMidi}
      />
      <button
        type="button"
        onClick={() => void handleRender()}
        disabled={!source || !detected || rendering}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
      >
        {rendering ? 'Rendering…' : '● Render'}
      </button>
    </div>
  );
}

